import { Link } from 'react-router';
import Footer from '../components/Footer';
import Nav from '../components/Nav';
import { useState, useEffect } from 'react';
import { useThemeColors } from '../hooks/useThemeColors';

export default function CookiePolicy() {
  const c = useThemeColors();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const sectionHeading: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: 22,
    color: c.textPrimary,
    margin: '40px 0 12px',
    lineHeight: 1.3,
  };

  const body: React.CSSProperties = {
    color: c.textMuted,
    fontSize: 16,
    lineHeight: '1.65',
    margin: '0 0 12px',
  };

  const listItem: React.CSSProperties = {
    color: c.textMuted,
    fontSize: 16,
    lineHeight: '1.65',
    margin: '0 0 8px',
  };

  const cookieName: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 14,
    color: c.textPrimary,
    fontWeight: 500,
  };

  return (
    <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
      <Nav scrolled={scrolled} />
      <div
        style={{
          maxWidth: 700,
          margin: '0 auto',
          padding: '96px 24px',
        }}
      >
        {/* Label */}
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            color: c.signal,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            margin: '0 0 16px',
          }}
        >
          LEGAL
        </p>

        {/* H1 */}
        <h1
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            fontSize: 48,
            letterSpacing: '-0.03em',
            color: c.textPrimary,
            margin: '0 0 12px',
            lineHeight: 1.1,
          }}
        >
          Cookie Policy
        </h1>

        <p style={{ ...body, margin: '0 0 32px' }}>
          Last updated: May 6, 2026
        </p>

        {/* Intro */}
        <p style={body}>
          This Cookie Policy explains how Primate AI, Inc. (doing business as "Primate Intelligence")
          uses cookies and similar technologies on primateintelligence.ai.
          It covers what cookies are, which ones we use, why we use them, and how you can control them.
        </p>

        {/* What are cookies */}
        <h2 style={sectionHeading}>What Are Cookies?</h2>
        <p style={body}>
          Cookies are small text files stored on your device when you visit a website. They help the site
          remember your actions and preferences — like login status and display settings — so you don't
          have to re-enter them each time you visit. Some cookies are set by us (first-party), and others
          are set by services we use (third-party).
        </p>

        {/* 1. Strictly Necessary */}
        <h2 style={sectionHeading}>1. Strictly Necessary Cookies</h2>
        <p style={body}>
          These cookies are essential for the site to function. Without them, features like authentication
          and security protections wouldn't work. You cannot opt out of these cookies.
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <span style={cookieName}>__clerk_db_jwt</span>,{' '}
            <span style={cookieName}>__session</span>,{' '}
            <span style={cookieName}>__client_uat</span> — Set by{' '}
            <strong style={{ color: c.textPrimary }}>Clerk</strong> (our authentication provider).
            These maintain your login session, verify your identity, and protect against unauthorized access.
            Duration: session to 1 year.
          </li>
          <li style={listItem}>
            <span style={cookieName}>__cf_bm</span>,{' '}
            <span style={cookieName}>__csrf_token</span> — CSRF protection and bot-detection cookies
            that keep your session secure. Duration: session to 30 minutes.
          </li>
        </ul>

        {/* 2. Functional */}
        <h2 style={sectionHeading}>2. Functional Cookies</h2>
        <p style={body}>
          Functional cookies remember your preferences to provide a better experience.
          They don't track you across other websites.
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <strong style={{ color: c.textPrimary }}>Theme and display preferences</strong> — We may
            store your preferred theme (light/dark) and UI settings locally so the site looks the way
            you left it. These are typically stored in your browser's local storage rather than as cookies.
          </li>
          <li style={listItem}>
            <strong style={{ color: c.textPrimary }}>Supabase</strong> — Our database provider may set
            functional cookies to manage authenticated API requests. Duration: session.
          </li>
        </ul>

        {/* 3. Analytics */}
        <h2 style={sectionHeading}>3. Analytics Cookies</h2>
        <p style={body}>
          Analytics cookies help us understand how visitors use the site — which pages are popular, where
          people drop off, and how we can improve. This data is aggregated and does not personally identify you.
          You can opt out of these cookies (see below).
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <span style={cookieName}>_ga</span>,{' '}
            <span style={cookieName}>_ga_*</span>,{' '}
            <span style={cookieName}>_gid</span> — Set by{' '}
            <strong style={{ color: c.textPrimary }}>Google Analytics 4 (GA4)</strong>.
            These distinguish unique users and sessions, and track page views and interactions.{' '}
            <span style={cookieName}>_ga</span> persists for up to 2 years;{' '}
            <span style={cookieName}>_gid</span> expires after 24 hours.
          </li>
          <li style={listItem}>
            <span style={cookieName}>ph_*</span> — Set by{' '}
            <strong style={{ color: c.textPrimary }}>PostHog</strong> (product analytics).
            These track feature usage, session replays, and product interactions to help us improve
            the user experience. Duration: up to 1 year.
          </li>
        </ul>

        {/* 4. Payment Processing */}
        <h2 style={sectionHeading}>4. Payment Processing Cookies</h2>
        <p style={body}>
          When you make a purchase or enter payment information, our payment processor sets cookies
          to prevent fraud and ensure a smooth checkout.
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <span style={cookieName}>__stripe_mid</span>,{' '}
            <span style={cookieName}>__stripe_sid</span> — Set by{' '}
            <strong style={{ color: c.textPrimary }}>Stripe</strong>.
            These detect fraudulent payment activity and link your browser session to the checkout flow.{' '}
            <span style={cookieName}>__stripe_mid</span> persists for 1 year;{' '}
            <span style={cookieName}>__stripe_sid</span> expires with your session.
          </li>
        </ul>

        {/* How to control */}
        <h2 style={sectionHeading}>How to Control Cookies</h2>
        <p style={body}>
          You have several options for managing cookies:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <strong style={{ color: c.textPrimary }}>Browser settings</strong> — Most browsers let you
            block or delete cookies through their privacy settings. Note that blocking strictly necessary
            cookies will prevent you from logging in or using core features.
          </li>
          <li style={listItem}>
            <strong style={{ color: c.textPrimary }}>Google Analytics opt-out</strong> — Install the{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: c.signal, textDecoration: 'none' }}
            >
              Google Analytics Opt-out Browser Add-on
            </a>{' '}
            to prevent GA4 from collecting your data.
          </li>
          <li style={listItem}>
            <strong style={{ color: c.textPrimary }}>PostHog opt-out</strong> — You can disable PostHog
            tracking by enabling "Do Not Track" in your browser settings. We respect the DNT signal
            for analytics collection.
          </li>
        </ul>

        {/* Third-party services */}
        <h2 style={sectionHeading}>Third-Party Services</h2>
        <p style={body}>
          Some cookies are placed by third-party services that appear on our pages. We do not control
          how these services use their cookies. For more information, review their privacy policies:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>
            <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: c.signal, textDecoration: 'none' }}>
              Clerk Privacy Policy
            </a>
          </li>
          <li style={listItem}>
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: c.signal, textDecoration: 'none' }}>
              Stripe Privacy Policy
            </a>
          </li>
          <li style={listItem}>
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: c.signal, textDecoration: 'none' }}>
              Google Privacy Policy
            </a>
          </li>
          <li style={listItem}>
            <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: c.signal, textDecoration: 'none' }}>
              PostHog Privacy Policy
            </a>
          </li>
        </ul>

        {/* Your rights */}
        <h2 style={sectionHeading}>Your Rights Under GDPR</h2>
        <p style={body}>
          If you're in the European Economic Area (EEA) or UK, you have the right to:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>
          <li style={listItem}>Access the personal data we hold about you</li>
          <li style={listItem}>Request correction or deletion of your data</li>
          <li style={listItem}>Object to or restrict processing of your data</li>
          <li style={listItem}>Withdraw consent for non-essential cookies at any time</li>
          <li style={listItem}>Lodge a complaint with your local data protection authority</li>
        </ul>
        <p style={body}>
          Withdrawing consent does not affect the lawfulness of processing carried out before withdrawal.
        </p>

        {/* Updates */}
        <h2 style={sectionHeading}>Changes to This Policy</h2>
        <p style={body}>
          We may update this Cookie Policy from time to time. When we do, we'll revise the "last updated"
          date at the top. We encourage you to check back periodically.
        </p>

        {/* Contact */}
        <h2 style={sectionHeading}>Contact Us</h2>
        <p style={{ ...body, margin: '0 0 40px' }}>
          If you have questions about our use of cookies or your privacy rights, email us at{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>
        </p>

        {/* Back link */}
        <Link
          to="/primate-vision"
          style={{ color: c.signal, fontSize: 14, textDecoration: 'none', fontWeight: 500 }}
        >
          ← Back to Primate Vision
        </Link>
      </div>
      <Footer />
    </div>
  );
}
